import { Component, DestroyRef, effect, inject } from '@angular/core';
import { DOCUMENT, CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { AiStore } from '../wh-ai.store';
import { TranslateModule } from '@ngx-translate/core';
import { WHImgAuthHeaderModule } from '@workheld/workheld-shared-lib';
import { setAiColor } from '../config/wh-ai-colors';

@Component({
  selector: 'app-wh-ai-button',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslateModule, WHImgAuthHeaderModule],
  template: `
    <button
      data-cy="wh-ai-button"
      (click)="toggleAiPanel()"
      class="wh-ai-button"
    >
      @if (aiStore.assistantLogoUrl(); as logoUrl) {
        <img
          data-cy="wh-ai-button-logo"
          class="wh-ai-button-logo"
          alt=""
          [src]="logoUrl | WHImgAuthHeader | async"
        />
      } @else {
        <mat-icon svgIcon="wh-ai-icon"></mat-icon>
      }
      <span
        data-cy="wh-ai-button-name"
        class="html-overflow wh-ai-button-name"
        >{{ aiStore.assistantName() }}</span
      >
    </button>
  `,
  styles: [
    `
      .wh-ai-button {
        all: unset;
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: center;
        background: var(--wh-ai-accent);
        border-radius: 32px;
        padding: 8px 16px;
        color: white;
        transition: 0.1s ease-in-out;
        font-weight: 600;

        svg {
          fill: white;
          stroke: white;
          width: 24px;
          height: 24px;
          path {
            stroke: white;
          }
        }
      }

      :host-context(.wh-ai-default-theme) .wh-ai-button {
        background: linear-gradient(
          135deg,
          var(--wh-ai-primary) 0%,
          var(--wh-ai-accent) 100%
        );
        box-shadow: inset 0 0 10px var(--wh-ai-accent);
      }

      .wh-ai-button-name {
        max-width: 200px;
      }

      .wh-ai-button-logo {
        width: 24px;
        height: 24px;
        object-fit: contain;
      }

      .wh-ai-button:hover {
        cursor: pointer;
        transform: scale(0.98);

        mat-icon {
          transform-origin: center;
          animation: spin 0.3s ease-in-out 1;
        }
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg) scale(1);
          filter: blur(0px);
        }
        50% {
          transform: rotate(90deg) scale(0.8);
          filter: blur(2px);
        }
        100% {
          transform: rotate(90deg) scale(1);
          filter: blur(0px);
        }
      }
    `,
  ],
})
export class WHAiButtonComponent {
  aiStore = inject(AiStore);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      if (this.aiStore.isDefaultTenant()) {
        this.document.body.classList.add('wh-ai-default-theme');
      } else {
        this.document.body.classList.remove('wh-ai-default-theme');
        const rootStyles = getComputedStyle(this.document.documentElement);
        const primaryColor = rootStyles
          .getPropertyValue('--primary-color-500')
          .trim();
        const accentColor = rootStyles
          .getPropertyValue('--accent-color-500')
          .trim();

        if (primaryColor) setAiColor('primary', primaryColor);
        if (accentColor) setAiColor('accent', accentColor);
      }
    });

    this.destroyRef.onDestroy(() => {
      this.document.body.classList.remove('wh-ai-default-theme');
    });
  }

  toggleAiPanel() {
    this.aiStore.toggleAiActive();
  }
}
